import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import { Card, FTBody, FTLabel, Page, Table } from '../Fuse';
import RegOptionsStore from '../../stores/RegOptionsStore';
import SnackStore from '../../stores/SnackStore';
import ConsoleStore from '../../stores/ConsoleStore';
import FuseInfoTooltip from '../FuseControls/FuseTooltip/FuseInfoTooltip';
import UDSpinner from '../UD/Fragments/UDSpinner';
import FuseButton from '../FuseControls/FuseButton';
import FuseAlert from '../FuseControls/FuseAlert';
import EmailRegStore from '../../stores/EmailRegStore';
import EmailDomainsModal from './EmailDomainsModal/EmailDomainsModal';
import BrowserRegistrationModal from './BrowserRegistrationModal/BrowserRegistrationModal';
import IpRangesModal from './IpRangesModal/IpRangesModal';
import IpRegStore from '../../stores/IpRegStore';
import { ROLE_READ_ONLY } from '../../stores/Administrators/AdministratorModalStore';
import './RegistrationOptions.css';

const block = b.lock('RegistrationOptions');

const SSO_CONFIGURATION_LINK = '/console/product_settings/saml_sso_configuration';

class RegistrationOptions extends Component {
    static propTypes = {
        regOptions: RegOptionsStore.PropType.isRequired,
        emailReg: EmailRegStore.PropType.isRequired,
        ipReg: IpRegStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
    };

    emailDomainTableColumns = [{
        name: 'emailDomain',
        title: 'Email Domain',
        render: (emailDomain) => {
            const { primaryDomain } = this.props.regOptions;
            return <FTBody>
                {emailDomain}
                {emailDomain === primaryDomain ? (
                    <span className={block('PrimaryAdminInfo')}>
                        (Primary Domain)
                        <FuseInfoTooltip>
                            The Primary Domain is associated with the email address of the Primary Admin.
                            Please contact support for any changes needed.
                        </FuseInfoTooltip>
                    </span>
                ) : null}
            </FTBody>;
        },
    }];

    ipRangesTableColumns = [{
        name: 'ipAddressSubnetwork',
        title: 'IP Address / Subnetwork',
        render: (ipRange) => <FTBody>{ipRange}</FTBody>,
    }];

    emailDomainTableActions = [{
        label: 'Edit',
        icon: 'edit',
        onPress: () => {
            const { emailReg } = this.props;
            emailReg.setFromObj(this.props.regOptions);
            emailReg.domainsModal.open();
        },
    }];

    ipRangesTableActions = [{
        label: 'Edit',
        icon: 'edit',
        onPress: () => {
            const { ipReg } = this.props;
            ipReg.setFromObj(this.props.regOptions);
            ipReg.modal.open();
        },
    }];

    openBrowserRegistrationModal = () => {
        const { emailReg } = this.props;
        emailReg.setFromObj(this.props.regOptions);
        emailReg.modal.open();
    };

    componentWillMount() {
        this.props.regOptions.load()
            .catch((error) => error && this.props.snack.showError(
                `Failed to load registration options. ${error.message}`));
    }

    renderBrowserRegistration = () => {
        const { regOptions, emailReg, console } = this.props;
        const readOnly = console.authority === ROLE_READ_ONLY.value;

        return <Card
            title="Browser Registration"
            titleAction={readOnly ? null : <FuseButton
                color="blue"
                icon="edit"
                disabled={regOptions.loadStatus.isLoading}
                onClick={this.openBrowserRegistrationModal}
            >Edit</FuseButton>}
        >
            <Fragment>
                {regOptions.loadStatus.isLoading || emailReg.configStatus.isLoading ? (
                    <UDSpinner type={'fuse'} className={block('Loader')} />
                ) : (
                    <Fragment>
                        <div className={block('Header')}>
                            <FTLabel>Registration Status</FTLabel>
                            <FuseInfoTooltip>
                                Allow the registration of new Browser/Email users. If disabled, any previously
                                registered
                                browsers will remain active.
                            </FuseInfoTooltip>
                        </div>

                        {regOptions.emailDisabled ? (
                            <FTBody><strong>Disabled</strong> - browser registration is disabled.</FTBody>
                        ) : (
                            <Fragment>
                                <FTBody><strong>Enabled</strong> - browser registration is enabled.</FTBody>

                                <div className={block('Header')}>
                                    <FTLabel>Registration Type</FTLabel>
                                    <FuseInfoTooltip>
                                        Specify the mechanism to use for new Browser/Email registrations.
                                    </FuseInfoTooltip>
                                </div>
                                {regOptions.useSsoLogin ? (
                                    <Fragment>
                                        <FTBody>
                                            <strong>SAML/SSO</strong> -
                                            users will be routed to the configured IdP for authentication.
                                        </FTBody>

                                        {!emailReg.samlSSOEnabled.isTrue ?
                                            <FuseAlert
                                                type={'warning'}
                                                title={'Warning -'}
                                                className={block('SSODisabledWarning')}
                                                subtitle={<Fragment>
                                                    <a className={block('Link')} href={SSO_CONFIGURATION_LINK}>
                                                        SAML/SSO Configuration
                                                    </a>
                                                    is currently disabled. Users will see an error while trying to
                                                    register.
                                                </Fragment>}
                                            /> : null}
                                    </Fragment>
                                ) : null}
                                {regOptions.useAccessCodes ? (
                                    <FTBody>
                                        <strong>Activation Codes</strong> -
                                        users will be emailed a one-time activation code to register their browser.
                                    </FTBody>
                                ) : null}
                                {!regOptions.useSsoLogin && !regOptions.useAccessCodes ? (
                                    <FTBody>
                                        <strong>Activation Links</strong> -
                                        users will be emailed a one-time activation link to register their browser.
                                    </FTBody>
                                ) : null}
                            </Fragment>
                        )}
                    </Fragment>
                )}
                <BrowserRegistrationModal />
                <EmailDomainsModal />
                <IpRangesModal />
            </Fragment>
        </Card>;
    };

    renderAssociatedEmailDomains = () => {
        const { regOptions, console } = this.props;
        const readOnly = console.authority === ROLE_READ_ONLY.value;

        return <Card noPadding>
            <Table
                cellClassName={block('EmailDomainTableCell')}
                header={<Fragment>
                    Associated Email&nbsp;
                    <div className={block('TitleWordWithInfo')}>
                        Domains
                        <FuseInfoTooltip>
                            These email domains will be used to lookup this account for registration and login routing.
                        </FuseInfoTooltip>
                    </div>
                </Fragment>}
                actions={readOnly ? null : this.emailDomainTableActions}
                pagination
                search={regOptions.domainSearch}
                searchPlaceholder={'Search by Email Domain'}
                columns={this.emailDomainTableColumns}
                items={regOptions.filteredDomains}
            />
        </Card>;
    };

    renderIpRegistration = () => {
        const { console, regOptions } = this.props;

        return <Card title={'IP Registration'}>
            <FTBody>
                Users connecting with these IP Addresses will have their browser registered automatically and assigned
                the default policy.
            </FTBody>
            {!regOptions.hashEmail ? <FuseAlert
                className={block('IpRegistrationAlert')}
                type={'warning'}
                title={console.isSuperAdmin ?
                    'IP Registration should only be used with Anonymous Registration enabled' :
                    'IP Registration must be configured and managed by your Proofpoint Administrator'}
            >
                {console.isSuperAdmin ? (
                    <div className={block('IpRegistrationAlertMessage')}>
                        {'This configuration is available at '}
                        <a href={'/superduper/customers/'} target={'_blank'} className={block('Link')}>
                            Superduper Console
                        </a>
                        under Customer Options.
                    </div>
                ) : 'Ask your Proofpoint administrator to enable IP Registration and provide a list of IP ' +
                    'Addresses you want to allow.'}
            </FuseAlert> : null}
        </Card>;
    };

    renderAssociatedIpRanges = () => {
        const { console, regOptions } = this.props;

        return <Card noPadding>
            <Table
                header={<Fragment>
                    Associated IP&nbsp;
                    <div className={block('TitleWordWithInfo')}>
                        Ranges
                        <FuseInfoTooltip>
                            These IP addresses will be used to automatically register a browser if there is a match.
                            All resulting browser traffic will be anonymized.
                        </FuseInfoTooltip>
                    </div>
                </Fragment>}
                columns={this.ipRangesTableColumns}
                searchPlaceholder={'Search by IP Address'}
                actions={console.isSuperAdmin && regOptions.hashEmail ? this.ipRangesTableActions : null}
                pagination
                search={regOptions.ipRangesSearch}
                items={regOptions.filteredIpRanges}
            />
        </Card>;
    };

    render() {
        const { console, regOptions } = this.props;

        return <Page title="Product Settings - Registration Options">
            {this.renderBrowserRegistration()}

            {!regOptions.loadStatus.isLoading ? (
                <Fragment>
                    {this.renderAssociatedEmailDomains()}
                    {console.isConsoleAdmin ? (
                        <Fragment>
                            {this.renderIpRegistration()}
                            {this.renderAssociatedIpRanges()}
                        </Fragment>
                    ) : null}
                </Fragment>
            ) : null}
        </Page>;
    }
}

export default inject('regOptions', 'emailReg', 'ipReg', 'snack', 'console')(observer(RegistrationOptions));
